/**
 * PlayerWorm — the MK VII "Eater of Worlds" player hull. The mothership becomes the HEAD
 * of a worm; body segments trail along the head's path (a follow-the-leader chain copied
 * from the EOW boss), ending in a tail. It:
 *   • mines by passing THROUGH asteroids/moons — any body a segment overlaps is consumed;
 *   • fires a high-damage beam straight out of the head while in combat;
 *   • mounts one auto-turret on every body + tail segment.
 *
 * The head is driven by the normal mothership movement (GameState.moveMothership); this
 * manager just records the head's trail, places the trailing segments, and runs the guns.
 */
import { Vector2 } from "@/math/Vector2";
import { MKVII, RIFT, COIL } from "@/config/balance";
import type { Damageable } from "@/game/combat/turrets";
import type { MineableBody } from "@/game/world/body";

export type PlayerWormPart = "head" | "body" | "tail";

export interface PlayerWormTurret {
  angle: number; // world aim angle
  cooldown: number;
  flash: number; // muzzle flash 0..1
}

export interface PlayerWormSegment {
  kind: PlayerWormPart;
  pos: Vector2;
  heading: number; // forward = toward the segment ahead (toward the head)
  turret: PlayerWormTurret | null; // head has none (it fires the maw beam instead)
  hidden: boolean; // Mk VIII rift transit: this segment is currently inside the rift (not drawn)
}

export interface PlayerWormBolt {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  dmg: number;
}

/** One rendered beam segment (mouth → tip) with a 0..1 age for the render fade. */
export interface PlayerWormBeamSeg {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  t: number;
}

export interface PlayerWormBeam {
  phase: "off" | "charge" | "big" | "fan";
  charge: number; // 0..1 charge progress during the "charge" phase
  fade: number; // 0..1 render alpha multiplier (fades out as the volley ends)
  main: PlayerWormBeamSeg | null; // the single big beam during the "big" phase
  fan: PlayerWormBeamSeg[]; // the 5 small beams during the "fan" phase
}

/** Mk VIII "Time & Space Rift" ability phases (see updateRift). */
export type RiftPhase =
  | "sweep" // laser sweeps left→right, carving a closed rift in front of the head
  | "wait" // 1s pause after the sweep ends
  | "opening" // the closed rift opens (vertical expand — a closed eye opening)
  | "enter" // the worm dives head-first INTO the entry rift, segments vanishing in turn
  | "emerge" // the worm slides OUT of the exit rift bit by bit at the target
  | "closing" // the exit rift shuts once the worm is fully out
  | "fading"; // the exit rift fades away

/** One end of the rift (entry in front of the head, exit at the minimap target). */
export interface PlayerWormRiftEnd {
  cx: number;
  cy: number;
  heading: number; // the worm's travel direction through this end (slit is perpendicular)
  open: number; // 0 closed (a slit) → 1 fully open (an eye)
  fade: number; // 0..1 render alpha
}

/** Live render + logic state for the rift ability. */
export interface PlayerWormRift {
  phase: RiftPhase;
  timer: number; // seconds elapsed in the current phase
  entry: PlayerWormRiftEnd;
  exit: PlayerWormRiftEnd;
  mawX: number; // sweep-laser origin (the head's maw at cast time)
  mawY: number;
  sweepFrac: number; // 0..1 sweep progress (grows the closed entry rift left→right)
  sweepBeam: { x0: number; y0: number; x1: number; y1: number } | null; // live sweep laser
}

/** Mk VIII "Maelstrom Coil" ability phases. */
export type CoilPhase = "forming" | "active";

/** One rendered coil laser (a segment shooting outward). */
export interface PlayerWormCoilLaser {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Live state for the Maelstrom Coil. */
export interface PlayerWormCoil {
  phase: CoilPhase;
  timer: number; // seconds elapsed in the current phase
  cx: number; // spiral centre (where the head winds in to) + becomes the shield centre
  cy: number;
  spinDir: number; // +1 / −1 — which way the spiral winds
  path: { x: number; y: number }[]; // the head's spiral path (fire pos → centre), sampled finely
  arc: number[]; // cumulative arc length along `path`
  totalArc: number; // the path's total arc (≈ the worm length)
  inx: number; // "incoming" unit dir (behind the head at cast) — extends the trail before the path
  iny: number;
  shieldR: number; // shield-bubble radius (set when the coil finishes forming)
  shieldHp: number; // remaining bubble HP (starts at COIL.shieldHp)
  swipe: { phase: number; freq: number; amp: number }[]; // per-segment laser wander
  lasers: PlayerWormCoilLaser[]; // render descriptors, rebuilt each active frame
}

/** Per-frame context the game hands the worm. */
export interface PlayerWormCtx {
  headX: number;
  headY: number;
  headHeading: number;
  foes: Damageable[]; // enemies the turrets + beam attack
  bodies: MineableBody[]; // rocks/moons the worm eats (passthrough + beam)
  combat: boolean; // (unused — the beam is now a manual ability)
  consumeBody: (b: MineableBody) => void; // credit ore + destroy a body
  onHit: (x: number, y: number) => void; // spawn a hit-spark VFX at a point
  beamDurMul: number; // beam-duration upgrade multiplier
  beamDmgMul: number; // beam-damage upgrade multiplier
}

/** Reduce a target's shield then hull (mirrors the turret/cannon damage model). */
function hurt(d: Damageable, dmg: number): void {
  if (d.shield > 0) {
    const after = d.shield - dmg;
    d.shieldFlash = 1;
    d.hitTimer = 0;
    if (after >= 0) {
      d.shield = after;
      return;
    }
    d.shield = 0;
    dmg = -after;
  }
  d.hp -= dmg;
  d.hitTimer = 0;
}

/** Perpendicular distance from a point to a segment. */
function distToSeg(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len2 = dx * dx + dy * dy || 1e-6;
  let t = ((px - x0) * dx + (py - y0) * dy) / len2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (x0 + dx * t), py - (y0 + dy * t));
}

export class PlayerWorm {
  readonly segments: PlayerWormSegment[] = [];
  readonly bolts: PlayerWormBolt[] = [];
  readonly beam: PlayerWormBeam = { phase: "off", charge: 0, fade: 1, main: null, fan: [] };
  private beamPhase: "idle" | "charge" | "big" | "fan" | "cooldown" = "idle";
  private beamTimer = 0; // seconds elapsed in the current beam phase
  private beamSpark = 0; // throttles hit-spark VFX emission
  private fanSwipe: { phase: number; freq: number; amp: number }[] = []; // per-split-beam wander
  // --- Mk VIII "Time & Space Rift" ability ---
  rift: PlayerWormRift | null = null; // live rift state (null when idle)
  private riftCooldown = 0; // seconds until the ability is ready again
  private riftSpark = 0; // throttles sweep-laser hit-spark VFX
  // --- Mk VIII "Maelstrom Coil" ability ---
  coil: PlayerWormCoil | null = null; // live coil state (null when idle)
  private coilCooldown = 0; // seconds until the coil is ready again
  private coilSpark = 0; // throttles coil-laser hit-spark VFX
  bodyCount: number;

  constructor(headX: number, headY: number, heading: number, bodyCount: number) {
    this.bodyCount = Math.max(1, bodyCount);
    this.build(headX, headY, heading);
  }

  /** (Re)build the chain extended straight back from the head along `heading`. */
  private build(hx: number, hy: number, heading: number): void {
    this.segments.length = 0;
    this.coil = null; // a rebuild (refit / segment upgrade) cancels any in-progress ability
    this.rift = null;
    const total = this.bodyCount + 2;
    for (let i = 0; i < total; i++) {
      const kind: PlayerWormPart = i === 0 ? "head" : i === total - 1 ? "tail" : "body";
      const back = i * MKVII.spacing;
      this.segments.push({
        kind,
        pos: new Vector2(hx - Math.cos(heading) * back, hy - Math.sin(heading) * back),
        heading,
        hidden: false,
        turret:
          kind === "head"
            ? null
            : { angle: heading, cooldown: Math.random() * MKVII.turretFireInterval, flash: 0 },
      });
    }
  }

  /** Change the body-segment count (mechanical-yard upgrade); rebuilds at the head. */
  setBodyCount(n: number, hx: number, hy: number, heading: number): void {
    n = Math.max(1, n);
    if (n === this.bodyCount) return;
    this.bodyCount = n;
    this.build(hx, hy, heading);
  }

  /** Trigger the maw beam (manual ability). Ignored unless the beam is idle/ready. */
  fire(): void {
    if (this.beamPhase === "idle") {
      this.beamPhase = "charge";
      this.beamTimer = 0;
    }
  }

  /** True when the beam can be fired (idle — not charging, firing, or cooling down). */
  get ready(): boolean {
    return this.beamPhase === "idle";
  }

  /** 0..1 fire-button fill: grows while charging, full while firing, drains through cooldown. */
  get busyFrac(): number {
    switch (this.beamPhase) {
      case "charge":
        return Math.min(1, this.beamTimer / MKVII.beamChargeTime);
      case "big":
      case "fan":
        return 1;
      case "cooldown":
        return Math.max(0, 1 - this.beamTimer / MKVII.beamCooldown);
      default:
        return 0;
    }
  }

  /** Smooth follow-the-leader: pull each segment to sit exactly `spacing` behind the one
   * ahead while keeping its current bearing — a continuous chain with no trail-step jitter. */
  private followChain(): void {
    // Follow-the-leader: each segment sits exactly `spacing` behind the one ahead, keeping its
    // bearing — the body just trails the head's path (a smooth follow-the-leader chain).
    for (let i = 1; i < this.segments.length; i++) {
      const ahead = this.segments[i - 1]!;
      const seg = this.segments[i]!;
      let dx = seg.pos.x - ahead.pos.x;
      let dy = seg.pos.y - ahead.pos.y;
      let d = Math.hypot(dx, dy);
      if (d < 1e-4) {
        dx = -Math.cos(ahead.heading);
        dy = -Math.sin(ahead.heading);
        d = 1;
      }
      const nx = dx / d;
      const ny = dy / d;
      seg.pos.set(ahead.pos.x + nx * MKVII.spacing, ahead.pos.y + ny * MKVII.spacing);
      seg.heading = Math.atan2(-ny, -nx); // face toward the segment ahead
    }
  }

  update(dt: number, ctx: PlayerWormCtx): void {
    if (this.riftCooldown > 0) this.riftCooldown = Math.max(0, this.riftCooldown - dt);
    if (this.coilCooldown > 0) this.coilCooldown = Math.max(0, this.coilCooldown - dt);
    // Maelstrom Coil: the worm is curled up in place driving its own segments + coil lasers, so
    // normal movement/follow/beam are suspended — but the segment TURRETS keep firing while active.
    if (this.coil) {
      const coilActive = this.coil.phase === "active";
      this.updateCoil(dt, ctx);
      if (coilActive) this.updateTurrets(dt, ctx); // turrets fire as normal while the lasers are out
      this.advanceBolts(dt, ctx);
      return;
    }
    // While the worm is diving through the rift (enter/emerge) it drives its OWN head; the
    // normal player-driven follow + weapons are suspended. Sweep/wait/opening freeze it in place.
    if (this.rift && this.riftControlsHead) {
      this.updateRift(dt, ctx);
      this.advanceBolts(dt, ctx);
      return;
    }
    const head = this.segments[0]!;
    head.pos.set(ctx.headX, ctx.headY);
    head.heading = ctx.headHeading;
    this.followChain();
    if (this.rift) this.updateRift(dt, ctx); // post-transit: the rift closes + fades behind the worm
    this.updateBeam(dt, ctx);
    this.updateTurrets(dt, ctx);
    this.mine(ctx);
    this.advanceBolts(dt, ctx);
  }

  // ----- Warp-jump transit (system→system jump cutscene) -----
  // The worm can't shrink into a point like the other hulls, so the jump cutscene drives it
  // THROUGH the rift head-first with the body following — the same plane-crossing hide used by
  // the Mk VIII rift ability, but parameterised for the warp cutscene's rift.

  /** Head→tail chain length in world units (how far the head must travel to thread the whole body). */
  get transitLength(): number {
    return Math.max(1, (this.segments.length - 1) * MKVII.spacing);
  }

  /** Current head world position (so the cutscene can keep the camera framed + sync the mothership). */
  get headPos(): { x: number; y: number } {
    const h = this.segments[0]!;
    return { x: h.pos.x, y: h.pos.y };
  }

  /** Cancel any in-progress ability + reveal all segments — called when a warp jump begins so
   * nothing renders frozen mid-cutscene. */
  stopForTransit(): void {
    this.beamPhase = "idle";
    this.beamTimer = 0;
    this.beam.phase = "off";
    this.beam.main = null;
    this.beam.fan.length = 0;
    this.coil = null;
    this.rift = null;
    for (const s of this.segments) s.hidden = false;
  }

  /** Warp "enter": drive the head straight through the rift plane at (cx,cy) facing `heading`;
   * each segment hides as it crosses. Returns true once the whole worm is inside the rift. */
  warpEnter(cx: number, cy: number, heading: number, speed: number, dt: number): boolean {
    const head = this.segments[0]!;
    head.pos.set(head.pos.x + Math.cos(heading) * speed * dt, head.pos.y + Math.sin(heading) * speed * dt);
    head.heading = heading;
    this.followChain();
    const nx = Math.cos(heading);
    const ny = Math.sin(heading);
    let allIn = true;
    for (const s of this.segments) {
      s.hidden = (s.pos.x - cx) * nx + (s.pos.y - cy) * ny > 0; // past the rift plane → inside
      if (!s.hidden) allIn = false;
    }
    return allIn;
  }

  /** Warp "emerge" setup: stack the whole chain straight behind the exit rift, all hidden. */
  warpEmergeStart(cx: number, cy: number, heading: number): void {
    const bx = Math.cos(heading);
    const by = Math.sin(heading);
    for (let i = 0; i < this.segments.length; i++) {
      const s = this.segments[i]!;
      s.pos.set(cx - bx * i * MKVII.spacing, cy - by * i * MKVII.spacing);
      s.heading = heading;
      s.hidden = true;
    }
  }

  /** Warp "emerge": slide the head out of the exit rift at (cx,cy); each segment reveals as it
   * crosses out. Returns true once the whole worm is out. */
  warpEmerge(cx: number, cy: number, heading: number, speed: number, dt: number): boolean {
    const head = this.segments[0]!;
    head.pos.set(head.pos.x + Math.cos(heading) * speed * dt, head.pos.y + Math.sin(heading) * speed * dt);
    head.heading = heading;
    this.followChain();
    const nx = Math.cos(heading);
    const ny = Math.sin(heading);
    let allOut = true;
    for (const s of this.segments) {
      s.hidden = (s.pos.x - cx) * nx + (s.pos.y - cy) * ny < 0; // still behind the rift plane → hidden
      if (s.hidden) allOut = false;
    }
    return allOut;
  }

  // ----- Mk VIII "Time & Space Rift" ability -----

  /** True while the rift is driving the worm through it (player control is suspended). */
  get riftControlsHead(): boolean {
    const p = this.rift?.phase;
    return p === "sweep" || p === "wait" || p === "opening" || p === "enter" || p === "emerge";
  }

  /** Can the ability be triggered right now? */
  get riftReady(): boolean {
    return !this.rift && !this.coil && this.riftCooldown <= 0;
  }

  /** 0..1 cooldown fill for the HUD button (1 = just used / mid-ability, 0 = ready). */
  get riftCooldownFrac(): number {
    if (this.rift) return 1;
    return this.riftCooldown > 0 ? Math.min(1, this.riftCooldown / RIFT.cooldown) : 0;
  }

  /** Distance from the head to the tail along the chain (used to time the dive/emerge). */
  private get wormLength(): number {
    return Math.max(1, (this.segments.length - 1) * MKVII.spacing);
  }

  /** Begin the rift: sweep a laser in front of the head, then teleport out at (exitX, exitY). */
  fireRift(exitX: number, exitY: number): void {
    if (!this.riftReady) return;
    // Cancel any in-progress maw beam so it doesn't render frozen during the transit.
    this.beamPhase = "idle";
    this.beamTimer = 0;
    this.beam.phase = "off";
    this.beam.main = null;
    this.beam.fan.length = 0;
    const head = this.segments[0]!;
    const h = head.heading;
    const cx = head.pos.x + Math.cos(h) * RIFT.frontDist;
    const cy = head.pos.y + Math.sin(h) * RIFT.frontDist;
    this.rift = {
      phase: "sweep",
      timer: 0,
      entry: { cx, cy, heading: h, open: 0, fade: 1 },
      exit: { cx: exitX, cy: exitY, heading: h, open: 0, fade: 0 },
      mawX: head.pos.x + Math.cos(h) * (MKVII.headWorldHeight * 0.4),
      mawY: head.pos.y + Math.sin(h) * (MKVII.headWorldHeight * 0.4),
      sweepFrac: 0,
      sweepBeam: null,
    };
  }

  /** Drive the whole rift sequence (see RiftPhase). */
  private updateRift(dt: number, ctx: PlayerWormCtx): void {
    const r = this.rift;
    if (!r) return;
    r.timer += dt;
    r.sweepBeam = null;
    this.riftSpark -= dt;
    const spark = this.riftSpark <= 0;
    if (spark) this.riftSpark = 0.04;
    // Perpendicular to the travel direction — the slit runs along this.
    const px = -Math.sin(r.entry.heading);
    const py = Math.cos(r.entry.heading);

    if (r.phase === "sweep") {
      r.sweepFrac = Math.min(1, r.timer / RIFT.sweepTime);
      // Sweep point runs from the left end (frac 0) to the right end (frac 1) of the slit.
      const off = RIFT.halfLen * (2 * r.sweepFrac - 1);
      const sx = r.entry.cx + px * off;
      const sy = r.entry.cy + py * off;
      r.sweepBeam = { x0: r.mawX, y0: r.mawY, x1: sx, y1: sy };
      this.riftSweepHit(r.mawX, r.mawY, sx, sy, ctx, spark);
      if (r.timer >= RIFT.sweepTime) {
        r.phase = "wait";
        r.timer = 0;
        r.sweepFrac = 1;
      }
    } else if (r.phase === "wait") {
      if (r.timer >= RIFT.openDelay) {
        r.phase = "opening";
        r.timer = 0;
      }
    } else if (r.phase === "opening") {
      r.entry.open = Math.min(1, r.timer / RIFT.openTime);
      if (r.timer >= RIFT.openTime) {
        r.entry.open = 1;
        r.phase = "enter";
        r.timer = 0;
      }
    } else if (r.phase === "enter") {
      // Drive the head forward through the entry rift; segments vanish as they cross the plane.
      const head = this.segments[0]!;
      const speed = (RIFT.frontDist + this.wormLength) / RIFT.enterTime;
      head.pos.set(
        head.pos.x + Math.cos(r.entry.heading) * speed * dt,
        head.pos.y + Math.sin(r.entry.heading) * speed * dt,
      );
      head.heading = r.entry.heading;
      this.followChain();
      const nx = Math.cos(r.entry.heading);
      const ny = Math.sin(r.entry.heading);
      let allIn = true;
      for (const s of this.segments) {
        s.hidden = (s.pos.x - r.entry.cx) * nx + (s.pos.y - r.entry.cy) * ny > 0;
        if (!s.hidden) allIn = false;
      }
      if (allIn || r.timer >= RIFT.enterTime + 0.5) {
        this.teleportToExit();
        r.phase = "emerge";
        r.timer = 0;
      }
    } else if (r.phase === "emerge") {
      // Drive the head out of the exit rift; segments reappear as they cross out, bit by bit.
      const head = this.segments[0]!;
      const speed = this.wormLength / RIFT.emergeTime;
      head.pos.set(
        head.pos.x + Math.cos(r.exit.heading) * speed * dt,
        head.pos.y + Math.sin(r.exit.heading) * speed * dt,
      );
      head.heading = r.exit.heading;
      this.followChain();
      const nx = Math.cos(r.exit.heading);
      const ny = Math.sin(r.exit.heading);
      let allOut = true;
      for (const s of this.segments) {
        s.hidden = (s.pos.x - r.exit.cx) * nx + (s.pos.y - r.exit.cy) * ny < 0;
        if (s.hidden) allOut = false;
      }
      r.entry.fade = Math.max(0, 1 - r.timer / (RIFT.emergeTime * 0.5)); // entry rift fades away
      if (allOut || r.timer >= RIFT.emergeTime + 0.5) {
        for (const s of this.segments) s.hidden = false;
        r.entry.fade = 0;
        r.phase = "closing";
        r.timer = 0;
      }
    } else if (r.phase === "closing") {
      r.exit.open = Math.max(0, 1 - r.timer / RIFT.closeTime);
      if (r.timer >= RIFT.closeTime) {
        r.exit.open = 0;
        r.phase = "fading";
        r.timer = 0;
      }
    } else if (r.phase === "fading") {
      r.exit.fade = Math.max(0, 1 - r.timer / RIFT.fadeTime);
      if (r.timer >= RIFT.fadeTime) {
        this.rift = null;
        this.riftCooldown = RIFT.cooldown;
      }
    }
  }

  /** Rebuild the chain stacked behind the exit rift (all hidden), ready to slide out. */
  private teleportToExit(): void {
    const r = this.rift!;
    const bx = Math.cos(r.exit.heading);
    const by = Math.sin(r.exit.heading);
    for (let i = 0; i < this.segments.length; i++) {
      const s = this.segments[i]!;
      s.pos.set(r.exit.cx - bx * i * MKVII.spacing, r.exit.cy - by * i * MKVII.spacing);
      s.heading = r.exit.heading;
      s.hidden = true;
    }
    r.exit.open = 1; // the exit rift pops open as the worm bursts out
    r.exit.fade = 1;
  }

  /** The sweep laser damages every foe it crosses and vaporizes any rock/moon along its line. */
  private riftSweepHit(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    ctx: PlayerWormCtx,
    spark: boolean,
  ): void {
    const dt = 1 / 60;
    for (const f of ctx.foes) {
      if (f.hp > 0 && distToSeg(f.pos.x, f.pos.y, x0, y0, x1, y1) < RIFT.sweepWidth + 24) {
        hurt(f, RIFT.sweepDps * dt);
        if (spark) ctx.onHit(f.pos.x, f.pos.y);
      }
    }
    for (const b of ctx.bodies) {
      if (b.ore > 0 && distToSeg(b.pos.x, b.pos.y, x0, y0, x1, y1) < RIFT.sweepWidth + b.radius) {
        ctx.consumeBody(b);
        ctx.onHit(b.pos.x, b.pos.y);
      }
    }
  }

  // ----- Mk VIII "Maelstrom Coil" ability -----

  /** True while the coil is running (movement is suspended). */
  get coilActive(): boolean {
    return this.coil !== null;
  }

  /** Can the coil be triggered now? (Not mid-rift, not on cooldown.) */
  get coilReady(): boolean {
    return !this.coil && !this.rift && this.coilCooldown <= 0;
  }

  /** 0..1 cooldown fill for the HUD button (1 = mid-ability / just used, 0 = ready). */
  get coilCooldownFrac(): number {
    if (this.coil) return 1;
    return this.coilCooldown > 0 ? Math.min(1, this.coilCooldown / COIL.cooldown) : 0;
  }

  /** True while the coil's shield bubble is up (its DoT + damage-soak are live). */
  get coilShieldUp(): boolean {
    return this.coil !== null && this.coil.phase === "active" && this.coil.shieldHp > 0;
  }

  /** The coil bubble soaks incoming player damage first; returns the overflow (unblocked). */
  coilAbsorb(dmg: number): number {
    const c = this.coil;
    if (!c || c.phase !== "active" || c.shieldHp <= 0) return dmg;
    const a = Math.min(c.shieldHp, dmg);
    c.shieldHp -= a;
    return dmg - a;
  }

  /** Begin the Maelstrom Coil: the head drives along a spiral (fire pos → centre) and the body
   * follows its trail, so the WHOLE worm winds in head-first. */
  fireCoil(): void {
    if (!this.coilReady) return;
    // Cancel any in-progress maw beam.
    this.beamPhase = "idle";
    this.beamTimer = 0;
    this.beam.phase = "off";
    this.beam.main = null;
    this.beam.fan.length = 0;
    const head = this.segments[0]!;
    const n = this.segments.length;
    const hh = head.heading;
    const spinDir = Math.random() < 0.5 ? 1 : -1;
    const wormLen = (n - 1) * MKVII.spacing;
    // Loops chosen so the radial gap between them ≈ spacing·loopGapMul (tight, length-independent).
    const loops = Math.max(1.5, Math.sqrt(wormLen / (MKVII.spacing * COIL.loopGapMul * Math.PI)));
    const R0 = wormLen / (loops * Math.PI); // starting radius — the head begins on the outer loop
    const phi = loops * Math.PI * 2; // total angle the head sweeps winding in
    // The head starts at the fire pos moving along `hh` (tangent to the outer circle) → the centre
    // sits perpendicular to the heading.
    const perpx = -Math.sin(hh) * spinDir;
    const perpy = Math.cos(hh) * spinDir;
    const cx = head.pos.x - perpx * R0;
    const cy = head.pos.y - perpy * R0;
    const angle0 = Math.atan2(head.pos.y - cy, head.pos.x - cx);
    // Sample the head's spiral path (fire pos → centre) with cumulative arc length.
    const path: { x: number; y: number }[] = [{ x: head.pos.x, y: head.pos.y }];
    const arc: number[] = [0];
    const STEPS = 220;
    let px = head.pos.x;
    let py = head.pos.y;
    let cum = 0;
    for (let k = 1; k <= STEPS; k++) {
      const t = k / STEPS;
      const ang = angle0 + spinDir * phi * t;
      const r = R0 * (1 - t);
      const x = cx + Math.cos(ang) * r;
      const y = cy + Math.sin(ang) * r;
      cum += Math.hypot(x - px, y - py);
      path.push({ x, y });
      arc.push(cum);
      px = x;
      py = y;
    }
    const swipe: { phase: number; freq: number; amp: number }[] = [];
    for (let i = 0; i < n; i++) {
      this.segments[i]!.hidden = false;
      swipe.push({
        phase: Math.random() * Math.PI * 2,
        freq:
          COIL.laserSwipeFreqMin + Math.random() * (COIL.laserSwipeFreqMax - COIL.laserSwipeFreqMin),
        amp: COIL.laserSwipeAmp * (0.5 + Math.random() * 0.5),
      });
    }
    this.coil = {
      phase: "forming",
      timer: 0,
      cx,
      cy,
      spinDir,
      path,
      arc,
      totalArc: cum,
      inx: -Math.cos(hh),
      iny: -Math.sin(hh),
      shieldR: R0 + COIL.shieldMargin, // provisional; refined when forming ends
      shieldHp: COIL.shieldHp,
      swipe,
      lasers: [],
    };
  }

  /** Point on the coil trail at arc length `s` from the head (s ≤ 0 extends the incoming line). */
  private coilPointAt(c: PlayerWormCoil, s: number): { x: number; y: number } {
    const path = c.path;
    if (s <= 0) return { x: path[0]!.x + c.inx * -s, y: path[0]!.y + c.iny * -s };
    if (s >= c.totalArc) {
      const last = path[path.length - 1]!;
      return { x: last.x, y: last.y };
    }
    const arc = c.arc;
    let k = 0;
    while (k < arc.length - 1 && arc[k + 1]! < s) k++;
    const a0 = arc[k]!;
    const a1 = arc[k + 1]!;
    const f = a1 > a0 ? (s - a0) / (a1 - a0) : 0;
    const p0 = path[k]!;
    const p1 = path[k + 1]!;
    return { x: p0.x + (p1.x - p0.x) * f, y: p0.y + (p1.y - p0.y) * f };
  }

  /** Face each body segment toward the one ahead so the coiled body reads as a continuous curve. */
  private orientCoil(): void {
    for (let i = 1; i < this.segments.length; i++) {
      const ahead = this.segments[i - 1]!;
      const seg = this.segments[i]!;
      const dx = ahead.pos.x - seg.pos.x;
      const dy = ahead.pos.y - seg.pos.y;
      if (dx * dx + dy * dy > 1e-6) seg.heading = Math.atan2(dy, dx);
    }
  }

  /** Drive the coil: the head spirals (leading) + the body follows → then hold + fire. */
  private updateCoil(dt: number, ctx: PlayerWormCtx): void {
    const c = this.coil;
    if (!c) return;
    c.timer += dt;
    c.lasers.length = 0;
    this.coilSpark -= dt;
    const spark = this.coilSpark <= 0;
    if (spark) this.coilSpark = 0.04;
    if (c.phase === "forming") {
      // The head drives along the spiral (fire pos → centre); every segment samples the head's trail
      // one spacing further back, so the WHOLE body traces the head's path into the coil, head-first.
      const p = Math.min(1, c.timer / COIL.formTime);
      const headArc = c.totalArc * (p * p * (3 - 2 * p)); // smoothstep the head's travel
      for (let i = 0; i < this.segments.length; i++) {
        const q = this.coilPointAt(c, headArc - i * MKVII.spacing);
        this.segments[i]!.pos.set(q.x, q.y);
      }
      this.orientCoil();
      if (c.timer >= COIL.formTime) {
        c.phase = "active";
        c.timer = 0;
        // Centre the shield on the coiled body + size it to enclose every segment.
        let sx = 0;
        let sy = 0;
        for (const s of this.segments) {
          sx += s.pos.x;
          sy += s.pos.y;
        }
        sx /= this.segments.length;
        sy /= this.segments.length;
        let maxD = 0;
        for (const s of this.segments) {
          const d = Math.hypot(s.pos.x - sx, s.pos.y - sy);
          if (d > maxD) maxD = d;
        }
        c.cx = sx;
        c.cy = sy;
        c.shieldR = maxD + COIL.shieldMargin;
      }
    } else {
      // Active: HOLD the coil pose (no segment is moved) and fire. Every segment shoots an outward
      // laser that wanders on its own (like the split maw beam); the shield bubble sears foes inside.
      const dps = MKVII.beamDps * COIL.laserDpsMul * ctx.beamDmgMul;
      const len = COIL.laserRange * Math.min(1, c.timer / COIL.laserGrowTime); // creep out like the maw beam
      for (let i = 0; i < this.segments.length; i++) {
        const s = this.segments[i]!;
        let dir = Math.atan2(s.pos.y - c.cy, s.pos.x - c.cx); // radially outward from the coil centre
        const sw = c.swipe[i]!;
        dir += Math.sin(c.timer * sw.freq + sw.phase) * sw.amp;
        const ex = s.pos.x + Math.cos(dir) * len;
        const ey = s.pos.y + Math.sin(dir) * len;
        c.lasers.push({ x0: s.pos.x, y0: s.pos.y, x1: ex, y1: ey });
        this.coilHit(s.pos.x, s.pos.y, ex, ey, dps, dt, ctx, spark);
      }
      if (c.shieldHp > 0) {
        const rr = c.shieldR * c.shieldR;
        for (const f of ctx.foes) {
          if (f.hp > 0 && (f.pos.x - c.cx) ** 2 + (f.pos.y - c.cy) ** 2 <= rr) {
            hurt(f, COIL.shieldDps * dt);
            if (spark) ctx.onHit(f.pos.x, f.pos.y);
          }
        }
      }
      if (c.timer >= COIL.activeTime) {
        // Done — the worm stays exactly where it is; normal movement uncoils it naturally.
        this.coil = null;
        this.coilCooldown = COIL.cooldown;
      }
    }
  }

  /** A coil laser damages every foe it crosses and vaporizes any rock/moon along its line. */
  private coilHit(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    dps: number,
    dt: number,
    ctx: PlayerWormCtx,
    spark: boolean,
  ): void {
    for (const f of ctx.foes) {
      if (f.hp > 0 && distToSeg(f.pos.x, f.pos.y, x0, y0, x1, y1) < COIL.laserWidth + 20) {
        hurt(f, dps * dt);
        if (spark) ctx.onHit(f.pos.x, f.pos.y);
      }
    }
    for (const b of ctx.bodies) {
      if (b.ore > 0 && distToSeg(b.pos.x, b.pos.y, x0, y0, x1, y1) < COIL.laserWidth + b.radius) {
        ctx.consumeBody(b);
        ctx.onHit(b.pos.x, b.pos.y);
      }
    }
  }

  /** The head's maw beam — a big beam that CREEPS out from the mouth (like the big cannons)
   * for beamBigDuration, then SPLITS into a fan of small beams for beamFanDuration, then
   * cools down and (while still in combat) fires the volley again. */
  private updateBeam(dt: number, ctx: PlayerWormCtx): void {
    const head = this.segments[0]!;
    const hx = Math.cos(head.heading);
    const hy = Math.sin(head.heading);
    const mx = head.pos.x + hx * (MKVII.headWorldHeight * 0.4); // the maw
    const my = head.pos.y + hy * (MKVII.headWorldHeight * 0.4);

    // Render descriptors rebuilt each frame.
    this.beam.phase = "off";
    this.beam.charge = 0;
    this.beam.fade = 1;
    this.beam.main = null;
    this.beam.fan.length = 0;
    this.beamSpark -= dt;
    const spark = this.beamSpark <= 0;
    if (spark) this.beamSpark = 0.05;

    if (this.beamPhase === "idle") return; // manual ability — waits for fire()
    this.beamTimer += dt;
    const bigDur = MKVII.beamBigDuration * ctx.beamDurMul; // duration upgrades stretch big + fan
    const fanDur = MKVII.beamFanDuration * ctx.beamDurMul;

    if (this.beamPhase === "charge") {
      // Charge telegraph at the maw — no damage yet.
      this.beam.phase = "charge";
      this.beam.charge = Math.min(1, this.beamTimer / MKVII.beamChargeTime);
      if (this.beamTimer >= MKVII.beamChargeTime) {
        this.beamPhase = "big";
        this.beamTimer = 0;
      }
    } else if (this.beamPhase === "big") {
      // One big beam creeping to full length, then holding for the rest of the phase.
      const len = MKVII.beamRange * Math.min(1, this.beamTimer / MKVII.beamGrowTime);
      const ex = mx + hx * len;
      const ey = my + hy * len;
      this.beam.phase = "big";
      this.beam.main = { x0: mx, y0: my, x1: ex, y1: ey, t: Math.min(1, this.beamTimer / bigDur) };
      this.beamHit(mx, my, ex, ey, MKVII.beamWidth, MKVII.beamDps * ctx.beamDmgMul, dt, ctx, spark);
      if (this.beamTimer >= bigDur) {
        this.beamPhase = "fan";
        this.beamTimer = 0;
        this.startFanSwipe(); // give each split beam its own random wander
      }
    } else if (this.beamPhase === "fan") {
      // The big beam splits into small beams that fan open gradually; EACH beam then wanders
      // on its OWN random swipe, spraying across the whole area.
      this.beam.phase = "fan";
      const n = MKVII.beamFanCount;
      const openFrac = Math.min(1, this.beamTimer / MKVII.beamFanOpenTime); // 0 → 1 slow open
      const spread = MKVII.beamFanSpread * openFrac;
      const half = spread / 2;
      const len = MKVII.beamRange * 0.9;
      const age = Math.min(1, this.beamTimer / fanDur);
      const left = fanDur - this.beamTimer;
      this.beam.fade = left < MKVII.beamFadeTime ? Math.max(0, left / MKVII.beamFadeTime) : 1;
      for (let i = 0; i < n; i++) {
        const frac = n === 1 ? 0.5 : i / (n - 1);
        const sw = this.fanSwipe[i];
        const swipe = sw ? Math.sin(this.beamTimer * sw.freq + sw.phase) * sw.amp : 0;
        const a = head.heading - half + frac * spread + swipe; // resting spot + its own wander
        const bx = Math.cos(a);
        const by = Math.sin(a);
        const ex = mx + bx * len;
        const ey = my + by * len;
        this.beam.fan.push({ x0: mx, y0: my, x1: ex, y1: ey, t: age });
        this.beamHit(mx, my, ex, ey, MKVII.beamFanWidth, MKVII.beamFanDps * ctx.beamDmgMul, dt, ctx, spark);
      }
      if (this.beamTimer >= fanDur) {
        this.beamPhase = "cooldown";
        this.beamTimer = 0;
      }
    } else if (this.beamPhase === "cooldown") {
      if (this.beamTimer >= MKVII.beamCooldown) {
        this.beamPhase = "idle"; // wait for the next manual trigger
        this.beamTimer = 0;
      }
    }
  }

  /** Roll fresh random swipe parameters (phase/rate/amplitude) for each split beam. */
  private startFanSwipe(): void {
    this.fanSwipe = [];
    for (let i = 0; i < MKVII.beamFanCount; i++) {
      this.fanSwipe.push({
        phase: Math.random() * Math.PI * 2,
        freq:
          MKVII.beamFanSwipeFreqMin +
          Math.random() * (MKVII.beamFanSwipeFreqMax - MKVII.beamFanSwipeFreqMin),
        amp: MKVII.beamFanSwipeAmp * (0.5 + Math.random() * 0.5),
      });
    }
  }

  /** Damage every foe along a beam segment and VAPORIZE any asteroid/moon it touches,
   * spraying hit-spark VFX at each contact. */
  private beamHit(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    width: number,
    dps: number,
    dt: number,
    ctx: PlayerWormCtx,
    spark: boolean,
  ): void {
    for (const f of ctx.foes) {
      if (f.hp > 0 && distToSeg(f.pos.x, f.pos.y, x0, y0, x1, y1) < width + 24) {
        hurt(f, dps * dt);
        if (spark) ctx.onHit(f.pos.x, f.pos.y);
      }
    }
    for (const b of ctx.bodies) {
      if (b.ore > 0 && distToSeg(b.pos.x, b.pos.y, x0, y0, x1, y1) < width + b.radius) {
        ctx.consumeBody(b); // the beam eats the rock whole (credits its ore)
        ctx.onHit(b.pos.x, b.pos.y);
      }
    }
  }

  /** Each body/tail turret tracks the nearest foe in range and fires bolts. */
  private updateTurrets(dt: number, ctx: PlayerWormCtx): void {
    const rangeSq = MKVII.turretRange * MKVII.turretRange;
    for (const seg of this.segments) {
      const tur = seg.turret;
      if (!tur) continue;
      tur.flash = Math.max(0, tur.flash - dt * 5);
      tur.cooldown -= dt;
      let best: Damageable | null = null;
      let bestD2 = rangeSq;
      for (const f of ctx.foes) {
        if (f.hp <= 0) continue;
        const d2 = (f.pos.x - seg.pos.x) ** 2 + (f.pos.y - seg.pos.y) ** 2;
        if (d2 < bestD2) {
          bestD2 = d2;
          best = f;
        }
      }
      if (!best) continue;
      // Lead the target: aim where it WILL be when the bolt arrives (like the normal turrets).
      const tvel = (best as { vel?: { x: number; y: number } }).vel;
      let aimX = best.pos.x;
      let aimY = best.pos.y;
      if (tvel) {
        const lead = Math.sqrt(bestD2) / MKVII.turretBoltSpeed;
        aimX += tvel.x * lead;
        aimY += tvel.y * lead;
      }
      const desired = Math.atan2(aimY - seg.pos.y, aimX - seg.pos.x);
      let diff = desired - tur.angle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      const step = 4 * dt;
      tur.angle += diff > step ? step : diff < -step ? -step : diff;
      if (tur.cooldown <= 0 && Math.abs(diff) < 0.25) {
        tur.cooldown = MKVII.turretFireInterval;
        tur.flash = 1;
        const bx = Math.cos(tur.angle);
        const by = Math.sin(tur.angle);
        this.bolts.push({
          x: seg.pos.x + bx * 18,
          y: seg.pos.y + by * 18,
          vx: bx * MKVII.turretBoltSpeed,
          vy: by * MKVII.turretBoltSpeed,
          life: MKVII.turretBoltLife,
          dmg: MKVII.turretDamage,
        });
      }
    }
  }

  /** Passthrough mining — any body a segment overlaps is consumed whole. */
  private mine(ctx: PlayerWormCtx): void {
    const r = MKVII.collisionRadius;
    for (const b of ctx.bodies) {
      if (b.ore <= 0) continue;
      for (const seg of this.segments) {
        const rr = r + b.radius;
        if ((b.pos.x - seg.pos.x) ** 2 + (b.pos.y - seg.pos.y) ** 2 <= rr * rr) {
          ctx.consumeBody(b);
          break;
        }
      }
    }
  }

  private advanceBolts(dt: number, ctx: PlayerWormCtx): void {
    const hitR = MKVII.turretHitRadius;
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let gone = b.life <= 0;
      if (!gone) {
        for (const f of ctx.foes) {
          if (f.hp <= 0) continue;
          if ((f.pos.x - b.x) ** 2 + (f.pos.y - b.y) ** 2 < hitR * hitR) {
            hurt(f, b.dmg);
            gone = true;
            break;
          }
        }
      }
      if (gone) this.bolts.splice(i, 1);
    }
  }

}
